// utils/screenshot.js

import axios from "axios";
import { uploadAssetWithDefaultFolder } from "./frontend_assetManager";
import { getFirestore, doc, getDoc, updateDoc, setDoc } from "firebase/firestore";

const CLOUD_RUN_SCREENSHOT_URL =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_LOCAL_SCREENSHOT_SERVICE_URL
    : process.env.REACT_APP_PROD_SCREENSHOT_SERVICE_URL;

/**
 * Requests a screenshot from the Cloud Run screenshot service.
 * @param {string} formId - ID of the form to screenshot.
 * @param {string} previewToken - Preview token for secure access.
 * @param {Object} sessionData - Optional sessionStorage payload to inject.
 * @returns {Promise<string>} - Screenshot base64 string (JPEG by default)
 */
export const requestScreenshotFromService = async (
  formId,
  previewToken,
  sessionData = {},
  format  = "jpeg",
  quality = 80
) => {
  if (!formId) throw new Error("Missing formId when requesting screenshot.");
  if (!previewToken) throw new Error("Missing previewToken when requesting screenshot.");

  try {
//    console.log("[utils/screenshot] Requesting screenshot from Cloud Run:", {
//      CLOUD_RUN_SCREENSHOT_URL,
//      formId,
//      previewToken,
//      keysInSessionData: Object.keys(sessionData),
//    });

    const response = await axios.post(
      `${CLOUD_RUN_SCREENSHOT_URL}/screenshot`,
      {                                 // body  ────────────────┐
        formId,
        previewToken,
        sessionData,
        width : 1100,       // css-px  (≤ the 1100 viewport in Cloud Run)
        format,              
        quality,             
        // height: 1200,    // (optional) send a height override
      },                                    
      { headers: { "Content-Type": "application/json" } }
    );

    if (!response?.data?.screenshot) {
      throw new Error("No screenshot returned from screenshot service.");
    }

    /** ──────────────────────────────────────────────────────────────
     *  The Cloud-Run service sometimes returns the JPEG as
     *  • a proper Base-64 string  **OR**
     *  • a CSV list of decimal byte values
     *
     *  The Firestore upload function **requires** valid Base-64, so
     *  we convert the CSV variant on the fly when we detect it.
     *  (We can’t use Node’s Buffer here – this is browser JS.)
     * ─────────────────────────────────────────────────────────────*/

    const rawShot = response.data.screenshot;

    // true when string contains commas and *only* digits / commas / whitespace
    const needsCsvConversion =
      typeof rawShot === "string" &&
      rawShot.includes(",") &&
      !/[A-Za-z+/=]/.test(rawShot);          // no Base-64 alphabet chars

    let base64;

    if (needsCsvConversion) {
//      console.log("[utils/screenshot] Converting CSV-bytes → Base-64…");

      // 1.  CSV  →  Uint8Array
      const byteArray = Uint8Array.from(
        rawShot
          .trim()
          .split(/\s*,\s*/)                 // allow spaces after commas
          .map((n) => Number.parseInt(n, 10)),
      );

      // 2.  Uint8Array  →  binary string
      let binary = "";
      for (let i = 0; i < byteArray.length; i++) {
        binary += String.fromCharCode(byteArray[i]);
      }

      // 3.  binary string  →  Base-64
      base64 = btoa(binary);                // browser-safe encoder
    } else {
      // already Base-64 – forward unchanged
      base64 = rawShot;
    }



//    console.log("[utils/screenshot] Screenshot received from Cloud Run.");
//    console.log("[utils/screenshot] Base64 preview (first 100 chars):", base64.slice(0, 100));
//    console.log("[utils/screenshot] Base64 length:", base64.length);

    return base64;
  } catch (err) {
    console.error("[utils/screenshot] Error calling screenshot service:", err);
    throw err;
  }
};

// Re-use a single Firestore handle for this module
const db = getFirestore();

/**
 * Build AUF bridge responses for screenshot → Asset Type field, if the
 * organization has a published Asset Upload Form + catalog.
 *
 * Returns either:
 *   { [assetTypeUfi]: { value: optionId }, [`label_${assetTypeUfi}`]: label }
 * or null when AUF/Asset Type is not configured.
 *
 * This mirrors the bridge logic used in AssetLibrary for icon/banner uploads.
 */
const buildScreenshotAssetTypeBridge = async (organizationId) => {
  if (!organizationId) return null;

  try {
    const orgRef = doc(db, "organizations", organizationId);
    const orgSnap = await getDoc(orgRef);
    if (!orgSnap.exists()) return null;

    const org = orgSnap.data() || {};
    const cat = org.assetFilterCatalog || null;
    if (!cat || !cat.labels || !cat.choices) return null;

    const labels  = cat.labels || {};
    const choices = cat.choices || {};
    const system  = cat.system || {};

    // -------------------------------------------------------------------------
    // Enterprise Asset Type resolution
    // -------------------------------------------------------------------------
    // Prefer the system registry produced by buildAssetFilterCatalog:
    //
    //   assetFilterCatalog.system.fields.assetType = {
    //     kind: "enum",
    //     ufi,
    //     map: { screenshot: optionId },
    //     reverse: { [optionId]: "screenshot" }
    //   }
    //
    // WHY
    //  • field labels can change ("Asset type" → "Content type")
    //  • option labels can change ("Screenshot" → "Product screenshot")
    //  • systemValue remains the durable platform-owned machine meaning
    //
    // Fallbacks remain below for legacy catalogs that do not yet have the
    // system registry or explicit systemValue.
    // -------------------------------------------------------------------------
    const assetTypeSystem = system?.fields?.assetType || null;

    let assetTypeUfi =
      typeof assetTypeSystem?.ufi === "string" &&
      assetTypeSystem.ufi.trim()
        ? assetTypeSystem.ufi.trim()
        : null;

    let screenshotOptionId =
      assetTypeSystem?.map?.screenshot != null
        ? String(assetTypeSystem.map.screenshot).trim()
        : "";

    // Legacy fallback 1:
    // Find the UFI for the canonical Asset Type field by label text.
    if (!assetTypeUfi) {
      for (const [ufi, label] of Object.entries(labels)) {
        if (
          typeof label === "string" &&
          label.trim().toLowerCase() === "asset type"
        ) {
          assetTypeUfi = ufi;
          break;
        }
      }
    }

    if (!assetTypeUfi) return null;

    const choiceArr = Array.isArray(choices[assetTypeUfi])
      ? choices[assetTypeUfi].filter(Boolean)
      : [];

    if (!choiceArr.length && !screenshotOptionId) return null;

    // Legacy fallback 2:
    // Resolve by explicit choice.systemValue first, then old label matching.
    if (!screenshotOptionId) {
      const systemMatch = choiceArr.find((opt) => {
        const sys =
          (opt?.systemValue || "")
            .toString()
            .trim()
            .toLowerCase();

        return sys === "screenshot";
      });

      const labelMatch = systemMatch || choiceArr.find((opt) => {
        const raw =
          (opt?.rawLabel || opt?.label || "")
            .toString()
            .trim()
            .toLowerCase();

        return raw === "screenshot";
      });

      screenshotOptionId =
        labelMatch?.value != null
          ? String(labelMatch.value).trim()
          : (
              labelMatch?.optionId != null
                ? String(labelMatch.optionId).trim()
                : ""
            );
    }

    if (!screenshotOptionId) {
      console.log(
        "[uploadScreenshotAsAsset] AUF bridge: " +
          "no Asset Type choice for systemValue 'screenshot'",
      );
      return null;
    }

    const label = labels[assetTypeUfi] || "Asset Type";
    const labelKey = `label_${assetTypeUfi}`;

    console.log("[uploadScreenshotAsAsset] AUF bridge for screenshot:", {
      ufi: assetTypeUfi,
      systemValue: "screenshot",
      optionId: screenshotOptionId,
      source: assetTypeSystem?.map?.screenshot ? "systemRegistry" : "fallback",
    });

    return {
      [assetTypeUfi]: { value: screenshotOptionId },
      [labelKey]: label,
    };
  } catch (e) {
    console.warn(
      "[uploadScreenshotAsAsset] AUF bridge failed (non-fatal):",
      e?.message || e,
    );
    return null;
  }
};

/**
 * Uploads a screenshot (received as a base64 string) and returns the asset metadata.
 * @param {string} file Base-64 string (raw **WebP**)
 * @param {string} organizationId - Organization ID.
 * @param {string} userId - User ID of the uploader.
 * @param {string} formId - ID of the form associated with the screenshot.
 * @param {Array<string>} userPermissions - User's permission array.
 * @param {string} authToken - Firebase Auth token.
 * @param {string} [screenshotStatus='published'] - Whether screenshot is from published or draft.
 * @returns {Promise<Object>} - Uploaded asset metadata (including thumbnail URL).
 */
export const uploadScreenshotAsAsset = async (
  file,
  organizationId,
  userId,
  formId,
  userPermissions,
  authToken,
  screenshotStatus = "published",
  additionalMetadata = {}
) => {
  if (!file || !organizationId || !userId || !formId || !authToken) {
    console.error("[uploadScreenshotAsAsset] Missing required parameters:", {
      hasFile: !!file,
      organizationId,
      userId,
      formId,
      hasAuthToken: !!authToken,
    });
    throw new Error("[uploadScreenshotAsAsset] Missing required parameters.");
  }

  const validStatus = screenshotStatus === "draft" ? "draft" : "published";

  const defaultFileName = `form_${formId}_preview.jpeg`;

  // 1) Start from caller-provided additionalMetadata
  const callerResponses =
    (additionalMetadata && additionalMetadata.responses) || null;

  // 2) AUF bridge: try to populate the canonical Asset Type field
  //    with the "screenshot" choice, when AUF is configured.
  const bridgeResponses =
    await buildScreenshotAssetTypeBridge(organizationId);

  let mergedResponses = callerResponses || null;
  if (bridgeResponses) {
    mergedResponses = {
      ...(callerResponses || {}),
      ...bridgeResponses,
    };
  }

  // 3) Final metadata sent to the backend:
  //    - base screenshot metadata
  //    - caller extras
  //    - merged AUF responses (if any)
  const metadata = {
    tags: ["screenshot", "formPreview"],
    relatedFormId: formId,
    status: validStatus,
    screenshotStatus: validStatus,
    screenshotCreatedAt: new Date().toISOString(),
    fileName: defaultFileName,
    ...additionalMetadata,
    ...(mergedResponses ? { responses: mergedResponses } : {}),
  };


//  console.log("[uploadScreenshotAsAsset] Uploading screenshot with metadata:", {
//    metadata,
//    organizationId,
//    userId,
//    permissions: userPermissions,
//  });

  try {
    const sanitizedBase64 = typeof file === "string" ? file.replace(/[\r\n\s]+/g, "") : file;

//    console.log(
//      "[uploadScreenshotAsAsset] Sanitized base64 (first 100 chars):",
//      typeof sanitizedBase64 === "string" ? sanitizedBase64.slice(0, 100) : "Not a string"
//    );
//    console.log(
//      "[uploadScreenshotAsAsset] Sanitized base64 length:",
//      typeof sanitizedBase64 === "string" ? sanitizedBase64.length : "N/A"
//    );

    const asset = await uploadAssetWithDefaultFolder(
      sanitizedBase64,
      organizationId,
      userId,
      metadata,
      null,
      userPermissions,
      "screenshot",
      authToken
    );

    if (asset?.versions?.thumbnail) {
//      console.log(
//        "[uploadScreenshotAsAsset] Upload succeeded. Thumbnail path:",
//        asset.versions.thumbnail.path
//      );
    } else {
      console.warn(
        "[uploadScreenshotAsAsset] Upload succeeded, but thumbnail object missing."
      );
    }

    return asset;
  } catch (err) {
    console.error("[uploadScreenshotAsAsset] Error during upload:", err);
    throw err;
  }
};


/**
 * High-level wrapper to generate and upload a screenshot with optional session data injection.
 * @param {Object} params - Screenshot input and metadata.
 * @param {string} params.formId
 * @param {string} params.previewToken
 * @param {Object} [params.sessionData] - Optional sessionStorage map to inject.
 * @param {string} params.organizationId
 * @param {string} params.userId
 * @param {Array<string>} params.userPermissions
 * @param {string} params.authToken
 * @param {string} [params.screenshotStatus]
 */
export const generateAndUploadScreenshot = async ({
  formId,
  previewToken,
  sessionData = {},
  organizationId,
  userId,
  userPermissions,
  authToken,
  screenshotStatus = "published",
  additionalMetadata = {},
  format   = "jpeg",   // new (default jpeg)
  quality  = 80, 
}) => {
//  console.log("[generateAndUploadScreenshot] Starting screenshot flow...");

  const injectedSessionData = {
    ...sessionData,
    isPublicPreview: true, 
  };

  // returns WebP base‑64
  const base64Screenshot = await requestScreenshotFromService(
    formId,
    previewToken,
    injectedSessionData,
    format,
    quality
  );

   const asset = await uploadScreenshotAsAsset(
     base64Screenshot,
     organizationId,
     userId,
     formId,
     userPermissions,
     authToken,
     screenshotStatus,
     additionalMetadata
   );


  /*──────────────────────────────────────────────────────────
    Persist Storage *paths* on the form so the client can
    grab the small thumbnail first and avoid the colour flicker
  ──────────────────────────────────────────────────────────*/

  const db = getFirestore();                      // 🆕 get a db handle
  const thumbPath = asset?.versions?.thumbnail?.path ?? null; // guard

 await setDoc(
   doc(db, "forms", formId),
   {
     screenshotPath      : asset.versions.original.path, // full-size
     screenshotThumbnail : thumbPath,                    // 400-px thumb
   },
   { merge: true }    // create stub if doc is missing, just merge if it exists
 );

  return asset;
 };
